import { DriverFeedbackItem, FollowUpEvent } from '../types/driverFeedback';
import { LinkStatus } from '../types/pickupSite';
import { DriverFeedbackItemDto } from '../graphql/queries/getDriverFeedbackListing';
import { FollowUpTimelineEventDto } from '../graphql/queries/getStationLinkTimeline';

/**
 * 后端 DriverFeedbackItemType / FollowUpTimelineEventType → 前端 DriverFeedbackItem / FollowUpEvent 适配。
 */

function buildDisplayAddress(dto: DriverFeedbackItemDto): string {
  const parts: string[] = [];
  const push = (v?: string | null) => {
    if (v && v.trim()) parts.push(v.trim());
  };
  push(dto.addressLine1);
  push(dto.addressLine2);
  push(dto.dock);
  push(dto.city);
  const stateZip = [dto.state?.trim(), dto.zipcode?.trim()].filter(Boolean).join(' ');
  if (stateZip) parts.push(stateZip);
  return parts.join(', ');
}

export function toDriverFeedbackItem(dto: DriverFeedbackItemDto, hubId: number): DriverFeedbackItem {
  const onSiteContact =
    dto.onsiteContactName || dto.onsiteContactPhone
      ? { name: dto.onsiteContactName ?? undefined, phone: dto.onsiteContactPhone ?? undefined }
      : undefined;
  const siteContact =
    dto.siteContactName || dto.siteContactPhone
      ? { name: dto.siteContactName ?? undefined, phone: dto.siteContactPhone ?? undefined }
      : undefined;
  return {
    id: dto.siteId ?? 0,
    warehouseId: hubId,
    siteId: dto.siteId ?? 0,
    siteName: dto.siteName ?? '',
    displayAddress: buildDisplayAddress(dto),
    addressLine1: dto.addressLine1 ?? undefined,
    city: dto.city ?? undefined,
    state: dto.state ?? undefined,
    zipcode: dto.zipcode ?? undefined,
    // 渠道名由 api-server 网关按 channelIdsJson 解析回填（enrichDriverFeedbackChannelNames）
    channelNames: dto.channelNames ?? [],
    linkStatus: (dto.linkStatus ?? LinkStatus.PickedUpPending) as LinkStatus,
    driverName: dto.driverName ?? '',
    feedbackTime: dto.feedbackTime ?? '',
    onSiteContacted: dto.onsiteContacted,
    onSiteContact,
    siteContact,
    driverNote: dto.driverNote ?? undefined,
    photoCount: dto.photoCount || 0,
    hasNewFeedback: dto.hasNewFeedback,
    siteCreatedAt: dto.siteCreatedAt ?? undefined,
    // 时间线在详情按需查（getStationLinkTimeline），列表不带
    timeline: [],
  };
}

/** operation_type → 时间线事件类型：5司机上门反馈→driver_feedback；3/6/7建联相关→ops_action；其余→system。 */
function mapEventType(operationType: number | null): FollowUpEvent['type'] {
  if (operationType === 5) return 'driver_feedback';
  if (operationType === 3 || operationType === 6 || operationType === 7) return 'ops_action';
  return 'system';
}

/**
 * 跟进条目该展示哪个联系人：现场回传优先，无现场回传时回退站点自身联系人（供电联首次建联）。
 *
 * 「未上门」阶段司机还没回传，只有站点联系人；列表卡片与右侧详情必须同口径，
 * 否则出现「卡片显示 - 、详情有联系人」。fromSite 供调用方决定是否打「站点联系人」标记。
 */
export function resolveFollowUpContact(item: DriverFeedbackItem): {
  contact?: { name?: string; phone?: string };
  fromSite: boolean;
} {
  const hasOnsite = !!(item.onSiteContact && (item.onSiteContact.name || item.onSiteContact.phone));
  if (hasOnsite) {
    return { contact: item.onSiteContact, fromSite: false };
  }
  const hasSite = !!(item.siteContact && (item.siteContact.name || item.siteContact.phone));
  return { contact: item.siteContact, fromSite: hasSite };
}

export function toFollowUpEvent(dto: FollowUpTimelineEventDto): FollowUpEvent {
  return {
    id: dto.id ?? 0,
    time: dto.time ?? '',
    type: mapEventType(dto.operationType),
    actor: dto.operatorName ?? '',
    content: dto.content ?? '',
  };
}
